import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

const PIE_SELECTOR = '.apexcharts-pie';
const MIN_PIE_SIZE_PX = 8;

export const getDonutCenterPosition = (
    pie: Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>,
    container: Pick<DOMRect, 'top' | 'left'>
): { top: number; left: number } | null => {
    if (pie.width < MIN_PIE_SIZE_PX || pie.height < MIN_PIE_SIZE_PX) {
        return null;
    }

    return {
        top: pie.top - container.top + pie.height / 2,
        left: pie.left - container.left + pie.width / 2
    };
};

const isSamePosition = (current: { top: number; left: number } | null, next: { top: number; left: number }): boolean =>
    current !== null && Math.abs(current.top - next.top) < 0.5 && Math.abs(current.left - next.left) < 0.5;

export const DonutCenterIcon = ({ children }: { children: ReactNode }) => {
    const iconRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

    useLayoutEffect(() => {
        const container = iconRef.current?.parentElement;
        if (!container) {
            return;
        }

        let frame = 0;

        const updatePosition = () => {
            const pie = container.querySelector(PIE_SELECTOR);
            if (!(pie instanceof Element)) {
                return;
            }

            const nextPosition = getDonutCenterPosition(pie.getBoundingClientRect(), container.getBoundingClientRect());
            if (!nextPosition) {
                return;
            }

            setPosition((current) => (isSamePosition(current, nextPosition) ? current : nextPosition));
        };

        const scheduleUpdate = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(updatePosition);
        };

        scheduleUpdate();

        const resizeObserver = new ResizeObserver(scheduleUpdate);
        resizeObserver.observe(container);

        const mutationObserver = new MutationObserver(scheduleUpdate);
        mutationObserver.observe(container, { childList: true, subtree: true, attributes: true });

        const timeouts = [100, 800].map((delay) => window.setTimeout(scheduleUpdate, delay));

        return () => {
            cancelAnimationFrame(frame);
            timeouts.forEach((timeout) => window.clearTimeout(timeout));
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, []);

    return (
        <div
            ref={iconRef}
            className='pointer-events-none absolute size-12 -translate-x-1/2 -translate-y-1/2'
            style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' }}>
            {children}
        </div>
    );
};
